import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Divider,
  MenuItem,
  Chip,
  Tooltip,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Add, Delete, WarningAmber, AutoFixHigh } from '@mui/icons-material';
import { canonicalTextHolesContent } from '../../utils/textHolesBlanks';

function ItemLLMCorrectionButton({ arrayKey, index, onCorrectItem, correctingItemKey, readOnly }) {
  const itemKey = `${arrayKey}-${index}`;
  const isLoading = correctingItemKey === itemKey;
  const isDisabled = readOnly || !onCorrectItem || (correctingItemKey && !isLoading);

  if (readOnly || !onCorrectItem) return null;

  return (
    <Tooltip title="Corriger cet élément via LLM">
      <span>
        <IconButton
          color="secondary"
          size="small"
          disabled={isDisabled}
          onClick={() => onCorrectItem(arrayKey, index)}
          aria-label="Corriger via LLM"
        >
          {isLoading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHigh fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/**
 * Édition structurée du contenu d'exercice (complément à l'éditeur JSON).
 * Met à jour l'objet `content` complet via onChange.
 */
const ExerciseContentVisualEditor = ({ gameType, content, onChange, readOnly, onCorrectItem, correctingItemKey }) => {
  const safe = content && typeof content === 'object' ? content : {};

  const patch = (partial) => {
    onChange({ ...safe, ...partial });
  };

  // —— speed_game ——
  if (gameType === 'speed_game') {
    const words = Array.isArray(safe.words) ? safe.words : [];

    const parseOptionsText = (value) => {
      const seen = new Set();
      return (value || '')
        .split('|')
        .map((s) => s.trim())
        .filter((s) => {
          if (!s || seen.has(s)) return false;
          seen.add(s);
          return true;
        });
    };

    const updateWord = (index, field, value) => {
      const next = [...words];
      const row = { ...(next[index] || {}) };
      if (field === 'optionsText') {
        row.optionsText = value;
        row.options = parseOptionsText(value);
      } else {
        row[field] = value;
      }
      next[index] = row;
      patch({ words: next });
    };

    const commitOptionsText = (index) => {
      const word = words[index];
      if (word?.optionsText == null) return;
      const next = [...words];
      const row = { ...next[index] };
      row.options = parseOptionsText(row.optionsText);
      delete row.optionsText;
      next[index] = row;
      patch({ words: next });
    };

    const addWord = () => {
      patch({
        words: [
          ...words,
          { correct: '', options: [], usagePhrase: '', readingPhrase: '', category: '' },
        ],
      });
    };

    const removeWord = (index) => {
      patch({ words: words.filter((_, i) => i !== index) });
    };

    const isCorrectMissingFromOptions = (word) => {
      const correct = (word.correct ?? '').trim();
      if (!correct) return false;
      const options = Array.isArray(word.options) ? word.options : [];
      return !options.includes(correct);
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Édition rapide (jeu de rapidité)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Options séparées par « | » (la bonne réponse doit figurer dans la liste).
        </Typography>
        {words.map((word, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Mot {index + 1}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ItemLLMCorrectionButton
                  arrayKey="words"
                  index={index}
                  onCorrectItem={onCorrectItem}
                  correctingItemKey={correctingItemKey}
                  readOnly={readOnly}
                />
                <IconButton
                  color="error"
                  onClick={() => removeWord(index)}
                  disabled={readOnly}
                  aria-label="Supprimer ce mot"
                >
                  <Delete />
                </IconButton>
              </Box>
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bonne réponse"
                  value={word.correct ?? ''}
                  onChange={(e) => updateWord(index, 'correct', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Options (séparées par | )"
                  value={
                    word.optionsText
                    ?? (Array.isArray(word.options) ? word.options.join(' | ') : '')
                  }
                  onChange={(e) => updateWord(index, 'optionsText', e.target.value)}
                  onBlur={() => commitOptionsText(index)}
                  disabled={readOnly}
                  helperText={`${(word.options || []).length} option(s)`}
                  InputProps={
                    isCorrectMissingFromOptions(word)
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="La bonne réponse n'est pas dans les options">
                                <WarningAmber color="warning" fontSize="small" />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phrase d'usage (avec trou)"
                  value={word.usagePhrase ?? ''}
                  onChange={(e) => updateWord(index, 'usagePhrase', e.target.value)}
                  disabled={readOnly}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phrase lue (réponse visible)"
                  value={word.readingPhrase ?? ''}
                  onChange={(e) => updateWord(index, 'readingPhrase', e.target.value)}
                  disabled={readOnly}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Catégorie"
                  value={word.category ?? ''}
                  onChange={(e) => updateWord(index, 'category', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button startIcon={<Add />} onClick={addWord} disabled={readOnly} variant="outlined" size="small">
          Ajouter un mot
        </Button>
      </Box>
    );
  }

  // —— text_holes ——
  if (gameType === 'text_holes') {
    const blanks = Array.isArray(safe.blanks) ? safe.blanks : [];
    const numHoles = (String(safe.text ?? '').match(/_{3,}/g) || []).length;
    const holeMismatch = numHoles > 0 && numHoles !== blanks.length;

    const patchTextHoles = (partial) => {
      onChange(canonicalTextHolesContent(safe, partial));
    };

    const updateBlank = (index, field, value) => {
      const next = blanks.map((b, i) => (i === index ? { ...b, [field]: value } : b));
      patchTextHoles({ blanks: next });
    };

    const updateDistractorsText = (index, value) => {
      const distractors = (value || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean);
      updateBlank(index, 'distractors', distractors);
    };

    const addBlank = () => {
      patchTextHoles({
        blanks: [...blanks, { answer: '', type: '', distractors: [] }],
      });
    };

    const removeBlank = (index) => {
      patchTextHoles({ blanks: blanks.filter((_, i) => i !== index) });
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Édition rapide (texte à trous)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Schéma : {`{ text, blanks: [{ answer, distractors[], type? }], completedText }`}.
          Utilisez <code>_____</code> (5 underscores ou plus) pour chaque trou, dans l&apos;ordre des blanks.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Texte avec trous"
          value={safe.text ?? ''}
          onChange={(e) => patchTextHoles({ text: e.target.value })}
          disabled={readOnly}
          sx={{ mb: 2 }}
          helperText={numHoles > 0 ? `${numHoles} trou(s) détecté(s)` : 'Aucun trou (_____) détecté'}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Texte complété (completedText)"
          value={safe.completedText ?? ''}
          onChange={(e) => patchTextHoles({ completedText: e.target.value })}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        {holeMismatch && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            {numHoles} trou(s) dans le texte mais {blanks.length} entrée(s) blanks — vérifiez la cohérence.
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Trous (blanks)
        </Typography>
        {blanks.map((row, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Trou {index + 1}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ItemLLMCorrectionButton
                  arrayKey="blanks"
                  index={index}
                  onCorrectItem={onCorrectItem}
                  correctingItemKey={correctingItemKey}
                  readOnly={readOnly}
                />
                <IconButton color="error" onClick={() => removeBlank(index)} disabled={readOnly}>
                  <Delete />
                </IconButton>
              </Box>
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Réponse (answer)"
                  value={row.answer ?? ''}
                  onChange={(e) => updateBlank(index, 'answer', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Type (verbe, préposition…)"
                  value={row.type ?? ''}
                  onChange={(e) => updateBlank(index, 'type', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Distracteurs (séparés par | )"
                  value={(row.distractors || []).join(' | ')}
                  onChange={(e) => updateDistractorsText(index, e.target.value)}
                  disabled={readOnly}
                  helperText={`${(row.distractors || []).length} distracteur(s)`}
                  InputProps={
                    !(row.distractors || []).length
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Chaque trou doit avoir au moins un distracteur">
                                <WarningAmber color="warning" fontSize="small" />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button startIcon={<Add />} onClick={addBlank} disabled={readOnly} variant="outlined" size="small">
          Ajouter un trou
        </Button>
      </Box>
    );
  }

  // —— correction_game ——
  if (gameType === 'correction_game') {
    const isIdentify = safe.interactionMode === 'identify'
      || (Array.isArray(safe.targets) && safe.targets.length > 0);
    const errors = Array.isArray(safe.errors) ? safe.errors : [];
    const targets = Array.isArray(safe.targets) ? safe.targets : [];
    // Planche VOULUE sans faute (réponse élève « Pas d'erreur »). Sans cet affichage, elle
    // est indiscernable d'une planche CASSÉE — les deux ont un `errors` vide, et c'est
    // exactement l'ambiguïté que le drapeau existe pour lever. Le relecteur doit la voir.
    const noErrors = safe.noErrors === true;

    const updateError = (index, field, value) => {
      const next = [...errors];
      next[index] = { ...(next[index] || {}), [field]: value };
      patch({ errors: next });
    };

    const updateTarget = (index, field, value) => {
      const next = [...targets];
      next[index] = { ...(next[index] || {}), [field]: value };
      patch({ targets: next });
    };

    const addError = () => {
      patch({
        errors: [...errors, { text: '', correction: '', explanation: '' }],
      });
    };

    const addTarget = () => {
      patch({
        targets: [...targets, { text: '', role: '', explanation: '' }],
      });
    };

    const removeError = (index) => {
      patch({ errors: errors.filter((_, i) => i !== index) });
    };

    const removeTarget = (index) => {
      patch({ targets: targets.filter((_, i) => i !== index) });
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Édition rapide (correction)
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }} disabled={readOnly}>
          <InputLabel>Mode d'interaction</InputLabel>
          <Select
            value={isIdentify ? 'identify' : 'correct_errors'}
            label="Mode d'interaction"
            onChange={(e) => {
              const mode = e.target.value;
              if (mode === 'identify') {
                patch({
                  interactionMode: 'identify',
                  errors: undefined,
                  targets: targets.length ? targets : [{ text: '', role: '', explanation: '' }],
                });
              } else {
                patch({
                  interactionMode: 'correct_errors',
                  targets: undefined,
                  errors: errors.length ? errors : [{ text: '', correction: '', explanation: '' }],
                });
              }
            }}
          >
            <MenuItem value="correct_errors">Correction d'erreurs (legacy)</MenuItem>
            <MenuItem value="identify">Repérage grammatical</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          multiline
          minRows={8}
          label={isIdentify ? 'Phrase correcte' : 'Texte avec erreurs'}
          value={safe.content ?? ''}
          onChange={(e) => patch({ content: e.target.value })}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        {!isIdentify && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant={noErrors ? 'contained' : 'outlined'}
              color={noErrors ? 'success' : 'inherit'}
              disabled={readOnly}
              onClick={() => patch(
                noErrors
                  // On repasse en planche fautive : une ligne d'erreur vide à remplir.
                  ? { noErrors: undefined, errors: errors.length ? errors : [{ text: '', correction: '', explanation: '' }] }
                  // On déclare la phrase correcte : plus aucun marqueur, par contrat.
                  : { noErrors: true, errors: [] },
              )}
            >
              {noErrors ? '✓ Phrase sans faute' : 'Déclarer « sans faute »'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {noErrors
                ? 'La bonne réponse est « Pas d’erreur ». Aucun marqueur attendu.'
                : 'Une phrase correcte doit être DÉCLARÉE : sans ce drapeau, un exercice sans marqueur est traité comme cassé.'}
            </Typography>
          </Box>
        )}
        {isIdentify && (
          <TextField
            fullWidth
            label="Énoncé"
            value={safe.enonce ?? ''}
            onChange={(e) => patch({ enonce: e.target.value })}
            disabled={readOnly}
            sx={{ mb: 2 }}
          />
        )}
        {isIdentify ? targets.map((target, index) => (
          <Paper key={`target-${index}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mot cible (text)"
                  value={target.text ?? ''}
                  onChange={(e) => updateTarget(index, 'text', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Rôle (nom, verbe, adverbe…)"
                  value={target.role ?? ''}
                  onChange={(e) => updateTarget(index, 'role', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Explication"
                  value={target.explanation ?? ''}
                  onChange={(e) => updateTarget(index, 'explanation', e.target.value)}
                  disabled={readOnly}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={1} sx={{ textAlign: 'right' }}>
                {!readOnly && (
                  <IconButton onClick={() => removeTarget(index)} size="small" color="error">
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Paper>
        )) : errors.map((err, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Erreur (texte)"
                  value={err.text ?? ''}
                  onChange={(e) => updateError(index, 'text', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Correction"
                  value={err.correction ?? ''}
                  onChange={(e) => updateError(index, 'correction', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Explication"
                  value={err.explanation ?? ''}
                  onChange={(e) => updateError(index, 'explanation', e.target.value)}
                  disabled={readOnly}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={1} sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <ItemLLMCorrectionButton
                  arrayKey="errors"
                  index={index}
                  onCorrectItem={onCorrectItem}
                  correctingItemKey={correctingItemKey}
                  readOnly={readOnly}
                />
                <IconButton color="error" onClick={() => removeError(index)} disabled={readOnly}>
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))}
        {/* Une planche déclarée sans faute n'accepte AUCUN marqueur : la certification
            backend refuse la contradiction. On retire donc le bouton plutôt que de
            laisser fabriquer un exercice qui sera rejeté à l'enregistrement. */}
        {!(noErrors && !isIdentify) && (
          <Button
            startIcon={<Add />}
            onClick={isIdentify ? addTarget : addError}
            disabled={readOnly}
            variant="outlined"
            size="small"
          >
            {isIdentify ? 'Ajouter une cible' : 'Ajouter une erreur'}
          </Button>
        )}
      </Box>
    );
  }

  // —— word_lists ——
  if (gameType === 'word_lists') {
    const rawWords = Array.isArray(safe.words) ? safe.words : [];
    const words = rawWords.map((entry) => {
      if (typeof entry === 'string') {
        return { word: entry, example: '', usagePhrase: '', hint: '' };
      }
      return {
        word: entry?.word ?? '',
        example: entry?.example ?? '',
        usagePhrase: entry?.usagePhrase ?? '',
        hint: entry?.hint ?? '',
      };
    });

    const updateWord = (index, field, value) => {
      const next = [...words];
      next[index] = { ...next[index], [field]: value };
      patch({ words: next });
    };

    const addWord = () => {
      patch({
        words: [...words, { word: '', example: '', usagePhrase: '', hint: '' }],
      });
    };

    const removeWord = (index) => {
      patch({ words: words.filter((_, i) => i !== index) });
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Édition rapide (liste de mots)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Chaque entrée : mot à apprendre, phrase d&apos;exemple, phrase avec trou (____) et indice.
        </Typography>
        {words.map((item, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Mot {index + 1}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ItemLLMCorrectionButton
                  arrayKey="words"
                  index={index}
                  onCorrectItem={onCorrectItem}
                  correctingItemKey={correctingItemKey}
                  readOnly={readOnly}
                />
                <IconButton
                  color="error"
                  onClick={() => removeWord(index)}
                  disabled={readOnly}
                  aria-label="Supprimer ce mot"
                >
                  <Delete />
                </IconButton>
              </Box>
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mot / expression"
                  value={item.word}
                  onChange={(e) => updateWord(index, 'word', e.target.value)}
                  disabled={readOnly}
                  placeholder="le chat, J'ai mangé…"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Indice (hint)"
                  value={item.hint}
                  onChange={(e) => updateWord(index, 'hint', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phrase d'exemple (lue à l'oral)"
                  value={item.example}
                  onChange={(e) => updateWord(index, 'example', e.target.value)}
                  disabled={readOnly}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phrase avec trou (____)"
                  value={item.usagePhrase}
                  onChange={(e) => updateWord(index, 'usagePhrase', e.target.value)}
                  disabled={readOnly}
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button startIcon={<Add />} onClick={addWord} disabled={readOnly} variant="outlined" size="small">
          Ajouter un mot
        </Button>
      </Box>
    );
  }

  // —— dictation (simple { text } ou guidée { text, wordChoices }) ——
  if (gameType === 'dictation') {
    const splitWords = (value) => (value || '').trim().split(/\s+/).filter(Boolean);

    const resolveText = () => {
      if (typeof safe.text === 'string' && safe.text.trim()) return safe.text;
      if (typeof safe.content === 'string' && safe.content.trim()) return safe.content;
      return '';
    };

    const text = resolveText();
    const wordChoices = Array.isArray(safe.wordChoices) ? safe.wordChoices : [];
    const words = splitWords(text);
    const isGuided = wordChoices.length > 0;
    const countMismatch = isGuided && wordChoices.length !== words.length;

    const emitDictation = (nextText, nextWordChoices) => {
      const payload = { text: nextText };
      if (nextWordChoices && nextWordChoices.length > 0) {
        payload.wordChoices = nextWordChoices;
      }
      onChange(payload);
    };

    const buildChoicesFromText = (sourceText, existing = wordChoices) => {
      const tokens = splitWords(sourceText);
      return tokens.map((word, index) => {
        const prev = existing.find((w) => w.wordIndex === index) ?? existing[index];
        const options = Array.isArray(prev?.options) && prev.options.length > 0
          ? [...prev.options]
          : [word, '', '', ''];
        while (options.length < 4) options.push('');
        return { wordIndex: index, options: options.slice(0, 4) };
      });
    };

    const updateText = (value) => {
      if (isGuided) {
        emitDictation(value, buildChoicesFromText(value, wordChoices));
      } else {
        emitDictation(value, []);
      }
    };

    const updateChoiceOptions = (index, optionsText) => {
      const parts = optionsText.split('|').map((s) => s.trim()).filter((s) => s !== '');
      while (parts.length < 4) parts.push('');
      const next = wordChoices.map((row, i) => (
        i === index ? { wordIndex: index, options: parts.slice(0, 4) } : row
      ));
      emitDictation(text, next);
    };

    const enableGuidedMode = () => {
      emitDictation(text, buildChoicesFromText(text));
    };

    const disableGuidedMode = () => {
      emitDictation(text, []);
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Édition rapide ({isGuided ? 'dictée guidée' : 'dictée simple'})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Format jeu : {`{ "text": "…", "wordChoices": [{ "wordIndex": 0, "options": ["correct", …] }] }`}.
          La 1<sup>re</sup> option de chaque mot est la bonne réponse.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Texte de la dictée"
          value={text}
          onChange={(e) => updateText(e.target.value)}
          disabled={readOnly}
          sx={{ mb: 2 }}
          helperText={words.length > 0 ? `${words.length} mot(s)` : 'Saisissez la phrase complète'}
        />
        {!readOnly && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!isGuided ? (
              <Button variant="outlined" size="small" onClick={enableGuidedMode} disabled={!text.trim()}>
                Activer le mode guidé (wordChoices)
              </Button>
            ) : (
              <Button variant="outlined" size="small" color="warning" onClick={disableGuidedMode}>
                Revenir à dictée simple (texte seul)
              </Button>
            )}
            {isGuided && (
              <Button variant="outlined" size="small" onClick={() => emitDictation(text, buildChoicesFromText(text))}>
                Resynchroniser les mots depuis le texte
              </Button>
            )}
          </Box>
        )}
        {countMismatch && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            {wordChoices.length} entrée(s) wordChoices pour {words.length} mot(s) — utilisez « Resynchroniser ».
          </Typography>
        )}
        {isGuided && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Choix par mot (wordChoices)
            </Typography>
            {wordChoices.map((row, index) => {
              const expectedWord = words[index] ?? `(mot ${index})`;
              const options = Array.isArray(row.options) ? row.options : [];
              return (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Mot {index + 1} — « {expectedWord} » (wordIndex: {row.wordIndex ?? index})
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Options (correct en 1er, séparées par | )"
                    value={options.join(' | ')}
                    onChange={(e) => updateChoiceOptions(index, e.target.value)}
                    disabled={readOnly}
                    helperText={`${options.filter(Boolean).length}/4 options`}
                  />
                </Paper>
              );
            })}
          </>
        )}
      </Box>
    );
  }

  if (gameType === 'association') {
    const pairs = Array.isArray(safe.pairs) ? safe.pairs : [];
    const updatePair = (index, field, value) => {
      const next = [...pairs];
      next[index] = { ...(next[index] || {}), [field]: value };
      patch({ pairs: next });
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Édition rapide (association)</Typography>
        <TextField
          fullWidth
          label="Énoncé"
          value={safe.enonce ?? ''}
          onChange={(e) => patch({ enonce: e.target.value })}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        {pairs.map((pair, index) => (
          <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="Gauche" value={pair.left ?? ''} onChange={(e) => updatePair(index, 'left', e.target.value)} disabled={readOnly} />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="Droite" value={pair.right ?? ''} onChange={(e) => updatePair(index, 'right', e.target.value)} disabled={readOnly} />
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
              <ItemLLMCorrectionButton
                arrayKey="pairs"
                index={index}
                onCorrectItem={onCorrectItem}
                correctingItemKey={correctingItemKey}
                readOnly={readOnly}
              />
              <IconButton color="error" onClick={() => patch({ pairs: pairs.filter((_, i) => i !== index) })} disabled={readOnly}>
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button startIcon={<Add />} onClick={() => patch({ pairs: [...pairs, { left: '', right: '' }] })} disabled={readOnly} variant="outlined" size="small">
          Ajouter une paire
        </Button>
      </Box>
    );
  }

  if (gameType === 'categorisation') {
    const categories = Array.isArray(safe.categories) ? safe.categories : [];
    const items = Array.isArray(safe.items) ? safe.items : [];
    const updateArrayItem = (key, collection, index, field, value) => {
      const next = [...collection];
      next[index] = { ...(next[index] || {}), [field]: value };
      patch({ [key]: next });
    };

    const isSpoken = safe.gestureStimulus === 'spoken';

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Édition rapide (catégorisation)</Typography>
        {isSpoken && (
          <Chip label="🔊 Mode écoute — contexte requis par item" size="small" color="info" variant="outlined" sx={{ mb: 1 }} />
        )}
        <TextField fullWidth label="Énoncé" value={safe.enonce ?? ''} onChange={(e) => patch({ enonce: e.target.value })} disabled={readOnly} sx={{ mb: 2 }} />
        <Typography variant="subtitle1" gutterBottom>Catégories</Typography>
        {categories.map((category, index) => (
          <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="ID" value={category.id ?? ''} onChange={(e) => updateArrayItem('categories', categories, index, 'id', e.target.value)} disabled={readOnly} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Libellé" value={category.label ?? ''} onChange={(e) => updateArrayItem('categories', categories, index, 'label', e.target.value)} disabled={readOnly} />
            </Grid>
            <Grid item xs={12} md={2}>
              <IconButton color="error" onClick={() => patch({ categories: categories.filter((_, i) => i !== index) })} disabled={readOnly}>
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button startIcon={<Add />} onClick={() => patch({ categories: [...categories, { id: '', label: '' }] })} disabled={readOnly} variant="outlined" size="small" sx={{ mb: 2 }}>
          Ajouter une catégorie
        </Button>
        <Typography variant="subtitle1" gutterBottom>Items</Typography>
        {items.map((item, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField fullWidth size="small" label="Texte" value={item.text ?? ''} onChange={(e) => updateArrayItem('items', items, index, 'text', e.target.value)} disabled={readOnly} />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField fullWidth size="small" label="ID catégorie correcte" value={item.correctCategoryId ?? ''} onChange={(e) => updateArrayItem('items', items, index, 'correctCategoryId', e.target.value)} disabled={readOnly} />
              </Grid>
              <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <ItemLLMCorrectionButton
                  arrayKey="items"
                  index={index}
                  onCorrectItem={onCorrectItem}
                  correctingItemKey={correctingItemKey}
                  readOnly={readOnly}
                />
                <IconButton color="error" onClick={() => patch({ items: items.filter((_, i) => i !== index) })} disabled={readOnly}>
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
            {isSpoken && (
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Contexte oral (lu)" placeholder="il va manger" value={item.spokenContext ?? ''} onChange={(e) => updateArrayItem('items', items, index, 'spokenContext', e.target.value)} disabled={readOnly} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Contexte écrit (affiché)" placeholder="il va ___" value={item.writtenContext ?? ''} onChange={(e) => updateArrayItem('items', items, index, 'writtenContext', e.target.value)} disabled={readOnly} />
                </Grid>
              </Grid>
            )}
          </Box>
        ))}
        <Button startIcon={<Add />} onClick={() => patch({ items: [...items, { text: '', correctCategoryId: '' }] })} disabled={readOnly} variant="outlined" size="small">
          Ajouter un item
        </Button>
      </Box>
    );
  }

  if (gameType === 'speed_sort') {
    const sortItems = Array.isArray(safe.sortItems) ? safe.sortItems : [];
    const updateSortItem = (index, field, value) => {
      const next = [...sortItems];
      next[index] = { ...(next[index] || {}), [field]: value };
      patch({ sortItems: next });
    };

    const isSpoken = safe.gestureStimulus === 'spoken';

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Édition rapide (speed sort)</Typography>
        {isSpoken && (
          <Chip label="🔊 Mode écoute — contexte requis par item" size="small" color="info" variant="outlined" sx={{ mb: 1 }} />
        )}
        <TextField fullWidth label="Énoncé" value={safe.enonce ?? ''} onChange={(e) => patch({ enonce: e.target.value })} disabled={readOnly} sx={{ mb: 2 }} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Libellé gauche" value={safe.leftLabel ?? ''} onChange={(e) => patch({ leftLabel: e.target.value })} disabled={readOnly} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Libellé droite" value={safe.rightLabel ?? ''} onChange={(e) => patch({ rightLabel: e.target.value })} disabled={readOnly} />
          </Grid>
        </Grid>
        {sortItems.map((item, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Item" value={item.text ?? ''} onChange={(e) => updateSortItem(index, 'text', e.target.value)} disabled={readOnly} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth size="small" label="Côté correct" value={item.correctSide ?? 'left'} onChange={(e) => updateSortItem(index, 'correctSide', e.target.value)} disabled={readOnly}>
                  <MenuItem value="left">left</MenuItem>
                  <MenuItem value="right">right</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <ItemLLMCorrectionButton
                  arrayKey="sortItems"
                  index={index}
                  onCorrectItem={onCorrectItem}
                  correctingItemKey={correctingItemKey}
                  readOnly={readOnly}
                />
                <IconButton color="error" onClick={() => patch({ sortItems: sortItems.filter((_, i) => i !== index) })} disabled={readOnly}>
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
            {isSpoken && (
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Contexte oral (lu)" placeholder="il va manger" value={item.spokenContext ?? ''} onChange={(e) => updateSortItem(index, 'spokenContext', e.target.value)} disabled={readOnly} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Contexte écrit (affiché)" placeholder="il va ___" value={item.writtenContext ?? ''} onChange={(e) => updateSortItem(index, 'writtenContext', e.target.value)} disabled={readOnly} />
                </Grid>
              </Grid>
            )}
          </Box>
        ))}
        <Button startIcon={<Add />} onClick={() => patch({ sortItems: [...sortItems, { text: '', correctSide: 'left' }] })} disabled={readOnly} variant="outlined" size="small">
          Ajouter un item
        </Button>
      </Box>
    );
  }

  // —— comprehension ——
  if (gameType === 'comprehension') {
    const passage = safe.passage && typeof safe.passage === 'object' ? safe.passage : {};
    const questions = Array.isArray(safe.questions) ? safe.questions : [];

    // Mode de rendu app (miroir de frontend/src/utils/passage.ts) : court → inline,
    // long → phase de lecture plein écran. Aide le relecteur à valider « les deux types ».
    const isShortPassage = (text) => {
      const t = (text || '').trim();
      if (!t) return false;
      const words = t.split(/\s+/).filter(Boolean).length;
      const sentences = t.split(/[.!?…]+/).map((s) => s.trim()).filter(Boolean).length;
      return sentences <= 2 && words <= 30;
    };
    const renderMode = passage.text
      ? (isShortPassage(passage.text)
          ? { label: 'Rendu app : inline (passage court)', color: 'info' }
          : { label: 'Rendu app : phase de lecture (passage long)', color: 'secondary' })
      : null;

    const parseOptionsText = (value) => {
      const seen = new Set();
      return (value || '')
        .split('|')
        .map((s) => s.trim())
        .filter((s) => {
          if (!s || seen.has(s)) return false;
          seen.add(s);
          return true;
        });
    };

    const updatePassage = (field, value) => {
      patch({ passage: { ...passage, [field]: value } });
    };

    const updateQuestion = (index, field, value) => {
      const next = [...questions];
      const row = { ...(next[index] || {}) };
      if (field === 'optionsText') {
        row.optionsText = value;
        row.options = parseOptionsText(value);
      } else {
        row[field] = value;
      }
      next[index] = row;
      patch({ questions: next });
    };

    const commitOptionsText = (index) => {
      const q = questions[index];
      if (q?.optionsText == null) return;
      const next = [...questions];
      const row = { ...next[index] };
      row.options = parseOptionsText(row.optionsText);
      delete row.optionsText;
      next[index] = row;
      patch({ questions: next });
    };

    const addQuestion = () => {
      patch({
        questions: [
          ...questions,
          { prompt: '', options: [], answer: '', notion: '', difficulty: '', sourceSpan: '' },
        ],
      });
    };

    const removeQuestion = (index) => {
      patch({ questions: questions.filter((_, i) => i !== index) });
    };

    // Le type d'interaction d'un item détermine SES champs. Sans ça l'éditeur traitait
    // tout en QCM : les paires d'association étaient invisibles (donc non éditables) et
    // les items span déclenchaient une fausse alerte « réponse absente des options »
    // alors qu'un span n'a pas d'options par nature.
    const itemTypeOf = (q) => {
      const t = (q?.itemType || 'qcm').toLowerCase();
      return ['qcm', 'span', 'cloze', 'association'].includes(t) ? t : 'qcm';
    };

    const updatePair = (qIndex, pairIndex, field, value) => {
      const q = questions[qIndex] || {};
      const pairs = Array.isArray(q.pairs) ? [...q.pairs] : [];
      pairs[pairIndex] = { ...(pairs[pairIndex] || {}), [field]: value };
      updateQuestion(qIndex, 'pairs', pairs);
    };
    const addPair = (qIndex) => {
      const q = questions[qIndex] || {};
      updateQuestion(qIndex, 'pairs', [...(Array.isArray(q.pairs) ? q.pairs : []), { left: '', right: '' }]);
    };
    const removePair = (qIndex, pairIndex) => {
      const q = questions[qIndex] || {};
      updateQuestion(qIndex, 'pairs', (Array.isArray(q.pairs) ? q.pairs : []).filter((_, i) => i !== pairIndex));
    };
    const updateBlank = (qIndex, blankIndex, field, value) => {
      const q = questions[qIndex] || {};
      const blanks = Array.isArray(q.blanks) ? [...q.blanks] : [];
      const row = { ...(blanks[blankIndex] || {}) };
      if (field === 'distractorsText') {
        row.distractorsText = value;
        row.distractors = value.split('|').map((x) => x.trim()).filter(Boolean);
      } else row[field] = value;
      blanks[blankIndex] = row;
      updateQuestion(qIndex, 'blanks', blanks);
    };

    const isAnswerMissingFromOptions = (q) => {
      if (itemTypeOf(q) !== 'qcm') return false; // span/cloze/association n'ont pas d'options
      const answer = (q.answer ?? '').trim();
      if (!answer) return false;
      const options = Array.isArray(q.options) ? q.options : [];
      return !options.includes(answer);
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Édition (compréhension)
        </Typography>

        {/* Passage porteur */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Passage porteur
            </Typography>
            {renderMode && (
              <Chip label={renderMode.label} size="small" color={renderMode.color} variant="outlined" />
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Titre (optionnel)"
                value={passage.title ?? ''}
                onChange={(e) => updatePassage('title', e.target.value)}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Texte du passage"
                value={passage.text ?? ''}
                onChange={(e) => updatePassage('text', e.target.value)}
                disabled={readOnly}
                multiline
                minRows={4}
              />
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Options séparées par « | » (la bonne réponse doit figurer dans la liste).
        </Typography>

        {questions.map((q, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Question {index + 1}
              </Typography>
              {/* Le type d'interaction conditionne les champs affichés ci-dessous. */}
              <Chip
                label={itemTypeOf(q)}
                size="small"
                variant="outlined"
                color={itemTypeOf(q) === 'qcm' ? 'default' : 'secondary'}
                sx={{ mr: 1 }}
              />
              <Box display="flex" alignItems="center" gap={0.5}>
                <ItemLLMCorrectionButton
                  arrayKey="questions"
                  index={index}
                  onCorrectItem={onCorrectItem}
                  correctingItemKey={correctingItemKey}
                  readOnly={readOnly}
                />
                <IconButton
                  color="error"
                  onClick={() => removeQuestion(index)}
                  disabled={readOnly}
                  aria-label="Supprimer cette question"
                >
                  <Delete />
                </IconButton>
              </Box>
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Question (prompt)"
                  value={q.prompt ?? ''}
                  onChange={(e) => updateQuestion(index, 'prompt', e.target.value)}
                  disabled={readOnly}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={5} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bonne réponse"
                  value={q.answer ?? ''}
                  onChange={(e) => updateQuestion(index, 'answer', e.target.value)}
                  disabled={readOnly}
                  InputProps={
                    isAnswerMissingFromOptions(q)
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="La bonne réponse n'est pas dans les options">
                                <WarningAmber color="warning" fontSize="small" />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
              </Grid>
              {itemTypeOf(q) === 'qcm' && (
                <Grid item xs={12} sm={7} md={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Options (séparées par | )"
                    value={
                      q.optionsText
                      ?? (Array.isArray(q.options) ? q.options.join(' | ') : '')
                    }
                    onChange={(e) => updateQuestion(index, 'optionsText', e.target.value)}
                    onBlur={() => commitOptionsText(index)}
                    disabled={readOnly}
                    helperText={`${(q.options || []).length} option(s)`}
                  />
                </Grid>
              )}

              {/* CLOZE : résumé troué (_____) + banque fermée par trou. */}
              {itemTypeOf(q) === 'cloze' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" multiline minRows={2}
                    label="Résumé troué (5 tirets bas par trou : _____)"
                    value={q.text ?? ''}
                    onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                    disabled={readOnly}
                    helperText={`${((q.text || '').match(/_{5,}/g) || []).length} trou(s) · ${(q.blanks || []).length} banque(s)`}
                  />
                  {(q.blanks || []).map((b, bi) => (
                    <Box key={bi} display="flex" gap={1} mt={1}>
                      <TextField
                        size="small" label={`Trou ${bi + 1} — réponse`}
                        value={b.answer ?? ''}
                        onChange={(e) => updateBlank(index, bi, 'answer', e.target.value)}
                        disabled={readOnly}
                      />
                      <TextField
                        fullWidth size="small" label="Distracteurs (séparés par | )"
                        value={b.distractorsText ?? (Array.isArray(b.distractors) ? b.distractors.join(' | ') : '')}
                        onChange={(e) => updateBlank(index, bi, 'distractorsText', e.target.value)}
                        disabled={readOnly}
                      />
                    </Box>
                  ))}
                </Grid>
              )}

              {/* ASSOCIATION : paires à relier (elles étaient invisibles ici). */}
              {itemTypeOf(q) === 'association' && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {(q.pairs || []).length} paire(s) à relier
                  </Typography>
                  {(q.pairs || []).map((pr, pi) => (
                    <Box key={pi} display="flex" gap={1} mt={1} alignItems="center">
                      <TextField
                        fullWidth size="small" label={`Gauche ${pi + 1}`}
                        value={pr.left ?? ''}
                        onChange={(e) => updatePair(index, pi, 'left', e.target.value)}
                        disabled={readOnly}
                      />
                      <TextField
                        fullWidth size="small" label={`Droite ${pi + 1}`}
                        value={pr.right ?? ''}
                        onChange={(e) => updatePair(index, pi, 'right', e.target.value)}
                        disabled={readOnly}
                      />
                      {!readOnly && (
                        <IconButton size="small" onClick={() => removePair(index, pi)} aria-label="Supprimer cette paire">
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {!readOnly && (
                    <Button size="small" onClick={() => addPair(index)} sx={{ mt: 1 }}>
                      + Ajouter une paire
                    </Button>
                  )}
                </Grid>
              )}
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notion"
                  value={q.notion ?? ''}
                  onChange={(e) => updateQuestion(index, 'notion', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Difficulté"
                  value={q.difficulty ?? ''}
                  onChange={(e) => updateQuestion(index, 'difficulty', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Span source (prélèvement / référent)"
                  value={q.sourceSpan ?? ''}
                  onChange={(e) => updateQuestion(index, 'sourceSpan', e.target.value)}
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button startIcon={<Add />} onClick={addQuestion} disabled={readOnly} variant="outlined" size="small">
          Ajouter une question
        </Button>
      </Box>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Aucun formulaire spécifique pour ce type de jeu — utilisez l’éditeur JSON ci-dessous.
    </Typography>
  );
};

export default ExerciseContentVisualEditor;
